var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CloseIcon, LoggedInIcon, LoggedOutIcon, MenuIcon, NNBrandIcon, SearchIcon, } from '@marketing-icons';
import Button from '@marketing/components/global/CTA/Button';
import { foregroundToBackgroundColorMap } from '@marketing/variables';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import DesktopMenu from '../DesktopMenu';
import MobileMenu from '../MobileMenu';
var closeMenuTimer = null;
function Header(_a) {
    var loggedIn = _a.loggedIn, _b = _a.backgroundColor, backgroundColor = _b === void 0 ? 'transparent' : _b, isSearchEnabled = _a.isSearchEnabled;
    var headerRef = useRef(null);
    var desktopMenuRef = useRef(null);
    var lastScrollPosition = useRef(0);
    var _c = useState(false), isSearchMenuOpen = _c[0], setIsSearchMenuOpen = _c[1];
    var _d = useState(false), isMobileMenuOpen = _d[0], setIsMobileMenuOpen = _d[1];
    // hasSelection for opening and closing the dropdown
    // currentSelection for which menu item is displayed
    var _e = useState(false), hasSelection = _e[0], setHasSelection = _e[1];
    var _f = useState(null), currentSelection = _f[0], setCurrentSelection = _f[1];
    // The header will slide up when the user scrolls down, and slide back down
    // when the user scrolls up
    var _g = useState(false), shouldHideHeader = _g[0], setShouldHideHeader = _g[1];
    var _h = useLocation(), pathname = _h.pathname, search = _h.search;
    var isSamplerPackPDP = pathname.endsWith('/sampler-pack');
    useEffect(function () {
        var overlay = document.getElementById('nav-shadow-overlay');
        if (overlay) {
            if (hasSelection && backgroundColor !== 'transparent') {
                overlay.style.setProperty('opacity', '1');
            }
            else {
                overlay.style.setProperty('opacity', '0');
            }
        }
    }, [hasSelection]);
    useEffect(function () {
        var closeMenuOnScrollOut = function () {
            requestAnimationFrame(function () {
                var _a, _b;
                if (headerRef.current &&
                    ((_a = desktopMenuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.submenu :focus').length) === 0) {
                    var headerHeight = headerRef.current.offsetHeight;
                    var scrollY_1 = window.scrollY || window.pageYOffset;
                    if (scrollY_1 > headerHeight * 2) {
                        if (scrollY_1 < lastScrollPosition.current) {
                            setShouldHideHeader(false);
                            lastScrollPosition.current = scrollY_1;
                        }
                        else {
                            if (scrollY_1 > lastScrollPosition.current + headerHeight) {
                                setShouldHideHeader(true);
                                lastScrollPosition.current = scrollY_1;
                            }
                        }
                        if (((_b = desktopMenuRef.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll('.submenu :focus').length) === 0) {
                            closeDropdown();
                        }
                    }
                }
            });
        };
        if (hasSelection) {
            var body_1 = document.querySelector('body');
            var bodyClickListener_1 = function (e) {
                if (headerRef.current && !headerRef.current.contains(e.target)) {
                    closeDropdown();
                    body_1.removeEventListener('click', bodyClickListener_1);
                }
            };
            body_1.addEventListener('click', bodyClickListener_1);
        }
        window.addEventListener('scroll', closeMenuOnScrollOut);
    }, [hasSelection, currentSelection, isMobileMenuOpen, isSearchMenuOpen]);
    useEffect(function () {
        if (shouldHideHeader) {
            setShouldHideHeader(false);
        }
    }, [pathname, search]);
    var dropdownTransitionDuration = 300; // in ms
    var backgroundColorStyle = '';
    if (backgroundColor === 'transparent') {
        // cross-browser workaround for a known Chromium bug where backdrop-filter
        // does not work on elements with parents who also have backdrop-filter
        // (see https://issues.chromium.org/issues/41475939 and
        // https://stackoverflow.com/questions/60997948/backdrop-filter-not-working-for-nested-elements-in-chrome/76207141#76207141)
        backgroundColorStyle =
            'bg-[rgba(255,255,255,0.5)] before:[backdrop-filter:blur(25px);] before:absolute before:w-full before:h-full before:top-0 before:left-0 before:bottom-0 before:right-0 before:z-[-1]';
    }
    else {
        backgroundColorStyle = foregroundToBackgroundColorMap[backgroundColor];
    }
    var closeDropdown = function () {
        if (closeMenuTimer) {
            clearTimeout(closeMenuTimer);
        }
        setHasSelection(false);
        setIsMobileMenuOpen(false);
        if (isSearchEnabled) {
            setIsSearchMenuOpen(false);
        }
    };
    useEffect(function () {
        var overlay = document.getElementById('nav-shadow-overlay');
        if (overlay) {
            if (hasSelection && backgroundColor !== 'transparent') {
                overlay.style.setProperty('opacity', '1');
            }
            else {
                overlay.style.setProperty('opacity', '0');
            }
        }
    }, [hasSelection]);
    var menuItemHeadings = ['Explore Plans', 'Our Meals', 'Other Products', 'Learn More'];
    var menuItems = menuItemHeadings.map(function (heading, index) {
        return (_jsx(DesktopMenu, { submenuIndex: index, heading: heading, hasSelection: hasSelection, currentSelection: currentSelection, backgroundColorStyle: backgroundColorStyle, onClickNavigationLink: function () {
                if (closeMenuTimer) {
                    clearTimeout(closeMenuTimer);
                }
                if (index === currentSelection && hasSelection) {
                    closeDropdown();
                }
                else {
                    setHasSelection(true);
                    setCurrentSelection(index);
                }
            }, onMouseEnterNavigationLink: function () {
                if (closeMenuTimer) {
                    clearTimeout(closeMenuTimer);
                }
                setHasSelection(true);
                setCurrentSelection(index);
            }, onClickSubmenu: function (e) {
                e.stopPropagation();
                // Close the dropdown if the user clicks on a link inside a submenu
                var linkElement = e.target.closest('a');
                if (linkElement !== null && linkElement.getAttribute('href')) {
                    closeDropdown();
                }
            } }, index));
    });
    return (_jsxs("header", __assign({ className: clsx('sticky left-0 top-0 w-full h-[64px] md:h-[92px] z-50 border-b border-shoe', 'transition-transform duration-300 ease-in-out', shouldHideHeader ? 'translate-y-[-100%]' : 'translate-y-0') }, { children: [_jsx("div", __assign({ className: clsx(backgroundColorStyle, 'w-full h-full') }, { children: _jsxs("nav", __assign({ ref: headerRef, "aria-label": "Primary navigation", className: clsx('w-full md:max-w-2xl h-full mx-auto z-20', 'px-xs-m py-none md:py-0 md:px-md-page-bottom-web flex shrink-0 justify-between items-center') }, { children: [_jsxs(Link, __assign({ viewTransition: true, className: "shrink-0", to: "/", onClick: function () {
                                closeDropdown();
                            } }, { children: [_jsx("span", __assign({ className: "sr-only" }, { children: "Link to Nom Nom Homepage" })), _jsx(NNBrandIcon, { className: "text-icon-dark w-[59px] md:w-[80px] h-[40px] md:h-[54px]" })] })), _jsxs("ul", __assign({ ref: desktopMenuRef, onMouseLeave: function () {
                                var _a;
                                if (closeMenuTimer) {
                                    clearTimeout(closeMenuTimer);
                                }
                                if (((_a = desktopMenuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.submenu :focus').length) === 0) {
                                    closeMenuTimer = setTimeout(function () {
                                        closeDropdown();
                                    }, 1000);
                                }
                            }, className: "hidden xl:full xl:flex h-full justify-between items-center gap-[60px] shrink-0 mx-[60px]" }, { children: [_jsx("li", { children: _jsx(Link, __assign({ viewTransition: true, onMouseEnter: closeDropdown, onClick: closeDropdown, to: "/why-nom-nom", className: clsx('h-full flex justify-center items-center box-border') }, { children: _jsx("p", __assign({ className: " !text-text-body text-lg-nav-d !font-display !inline" }, { children: "Why Nom Nom" })) })) }), menuItems] })), _jsxs("div", __assign({ className: "flex justify-between items-center gap-9 md:gap-8" }, { children: [_jsx("div", __assign({ className: "flex justify-center items-center h-full w-[27px]" }, { children: _jsx("a", __assign({ href: clsx(loggedIn ? '/account' : '/account/login') }, { children: loggedIn ? (_jsx(LoggedInIcon, { className: "text-icon-white border-icon-dark fill-icon-dark size-[27px] md:size-7 shrink-0" })) : (_jsx(LoggedOutIcon, { className: "text-icon-dark size-[31px] md:size-[32px] shrink-0" })) })) })), isSearchEnabled && (_jsx("div", __assign({ className: "flex justify-center items-center h-[27px] w-[27px]" }, { children: _jsx("button", __assign({ className: "flex justify-center items-center w-full h-full", onClick: function () {
                                            setIsSearchMenuOpen(!isSearchMenuOpen);
                                            if (isMobileMenuOpen) {
                                                setIsMobileMenuOpen(false);
                                            }
                                        } }, { children: isSearchMenuOpen ? (_jsx(CloseIcon, { className: "text-icon-dark" })) : (_jsx(SearchIcon, { className: "text-icon-dark size-[28px] shrink-0" })) })) }))), _jsx("div", __assign({ className: "w-[27px] xl:hidden flex justify-center items-center h-[27px]" }, { children: _jsx("button", __assign({ className: "flex justify-center items-center w-full h-full", onClick: function () {
                                            setIsMobileMenuOpen(!isMobileMenuOpen);
                                            if (isSearchEnabled && isSearchMenuOpen) {
                                                setIsSearchMenuOpen(false);
                                            }
                                        } }, { children: isMobileMenuOpen ? (_jsx(CloseIcon, { className: "text-icon-dark" })) : (_jsx(MenuIcon, { className: "text-icon-dark" })) })) })), _jsx("div", __assign({ className: "hidden xl:w-full xl:flex justify-center items-center h-10" }, { children: loggedIn ? (_jsx("a", __assign({ href: '/account', className: "h-full hover:no-underline" }, { children: _jsx(Button, { use: "funnel", size: "small", label: 'Account', className: "h-full" }) }))) : (_jsx(Link, __assign({ to: "/profile".concat(isSamplerPackPDP ? '/otto' : ''), viewTransition: true, className: "h-full hover:no-underline" }, { children: _jsx(Button, { use: "funnel", size: "small", label: isSamplerPackPDP ? 'Checkout Now' : 'Get Started', className: "h-full" }) }))) }))] }))] })) })), _jsx("div", __assign({ onClick: function (e) {
                    e.stopPropagation();
                    // if a link inside a submenu is clicked, close the dropdown
                    var linkElement = e.target.closest('a');
                    if (linkElement !== null && linkElement.getAttribute('href')) {
                        closeDropdown();
                    }
                }, className: "absolute h-0 left-0 w-full top-[64px] md:top-[92px]" }, { children: _jsx("div", __assign({ className: "block pointer-events-none w-full overflow-hidden xl:hidden" }, { children: _jsx(MobileMenu, { backgroundColorStyle: backgroundColorStyle, isExpanded: isMobileMenuOpen, transitionDuration: dropdownTransitionDuration }) })) }))] })));
}
export default Header;
